import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import {
  CardPickerButton,
  applyChoice,
  useCardChoice,
  type CardChoice,
} from './CardPicker';

/**
 * بطاقات صدر الملفّ.
 *
 * هي بطاقات القوائم نفسها شكلًا — الدائرة الملوَّنة والعنوان والقيمة
 * والظهور المتتابع — لأن من يفتح ملفّ عميل قادمٌ من قائمة العملاء، وبطاقتان
 * تحملان المعنى نفسه بشكلين مختلفين تجعلانه يقرأ الشاشة من جديد.
 *
 * والفارق الوحيد أن قيمتها ليست عددًا دائمًا: قد تكون اسم مسؤول أو موعدًا أو
 * «لم يتم». ومن هنا جاءت الحاجة إلى مكوّن ثانٍ لا إلى إعادة استعمال
 * `InsightCards` كما هي — تلك تعدّ وتُصفّي، وهذه تعرض حقيقةً عن سجلٍّ واحد.
 *
 * **ولمَ لا نُعدّل `Stat` نفسها؟** لأنها تُستعمل في أربعٍ وثلاثين شاشة —
 * لوحات المالية والموارد البشرية والأصول — وتغييرُ شكلها هنا يغيّرها هناك
 * لسببٍ لا يخصّها.
 */

export interface RecordStat {
  key: string;
  label: string;
  /** المجموعة في نافذة الاختيار — «المتابعة» أو «المالية». */
  group: string;
  icon: IconName;
  color: string;
  value: ReactNode;
  note?: string;
  /**
   * قيمةٌ رقمية.
   *
   * الأرقام تأخذ خطّ الأرقام بأعمدةٍ ثابتة العرض وحجمًا أكبر، والنصّ يأخذ
   * خطّ الواجهة. وخلطُهما هو ما كان يجعل «اجتماع تعارف — ٣٠ دقيقة — محمود
   * عبد الرحمن» يُصاغ بخطّ الأرقام في ثلاثة أسطر.
   */
  numeric?: boolean;
  hint?: string;
}

export function RecordStats({
  items,
  storageKey,
  defaults,
  external,
}: {
  /** كل ما تعرفه الشاشة — والمستخدم يختار منه. */
  items: RecordStat[];
  /** مفتاح الحفظ في المتصفح — واحدٌ لكل نوع سجلّ. */
  storageKey: string;
  defaults: string[];
  /**
   * حالةُ اختيارٍ مرفوعةٌ إلى الصفحة.
   *
   * حين يكون زرّ «البطاقات» في شريط أدوات الصفحة هو المنتقي (كقوائم
   * إدارة العملاء)، تُمرَّر حالتُه هنا ويُخفى منتقي الذيل — منتقيان
   * للشيء نفسه يعني حالتين تفترقان.
   */
  external?: { chosen: string[]; setChosen: (keys: string[]) => void; reset: () => void };
}) {
  const internal = useCardChoice(storageKey, defaults);
  const { chosen, setChosen, reset } = external ?? internal;

  const shown = applyChoice(items, chosen);

  const choices: CardChoice[] = items.map(({ key, label, group }) => ({
    key,
    label,
    group,
  }));

  return (
    <div className="insights rstats">
      {shown.map((item, index) => (
        <div
          key={item.key}
          className="insight insight-static rstat"
          title={item.hint}
          style={{
            ['--insight-color' as string]: item.color,
            /* التتابع نفسه المُستعمل في القوائم: البطاقات تصعد واحدةً بعد الأخرى. */
            ['--insight-delay' as string]: `${index * 45}ms`,
          }}
        >
          <span className="insight-icon">
            <Icon name={item.icon} size={17} />
          </span>

          <span className="insight-body">
            <span className="insight-label">{item.label}</span>
            <span className={`rstat-value${item.numeric ? ' num' : ''}`}>
              {item.value}
            </span>
            {item.note && <span className="rstat-note">{item.note}</span>}
          </span>
        </div>
      ))}

      {/*
        المُنتقي بطاقةٌ في ذيل الصفّ لا زرٌّ فوقه.
        صدر الملفّ لا شريط أدوات له، وزرٌّ عائم فوق البطاقات يحجب أوّلها.
        وحين يكون المنتقي في شريط الصفحة (external) لا يُكرَّر هنا.
      */}
      {!external && (
        <CardPickerButton
          choices={choices}
          chosen={chosen}
          onChange={setChosen}
          onReset={reset}
          trigger="tile"
        />
      )}
    </div>
  );
}
